import { v4 as uuidv4 } from 'uuid';
import {
  CorrespondenceStatus,
  createCorrespondenceData,
  transitionToSent,
  transitionFromSent,
  transitionToCancelled,
  transitionFromCancelled,
  isPrinted as checkIsPrinted,
  isSent as checkIsSent,
  isCancelled as checkIsCancelled
} from '@strenforcement/domain/correspondence';

// Re-export for backward compatibility
export const STATUS = CorrespondenceStatus;

function isObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function stableNormalize(value) {
  if (Array.isArray(value)) {
    return value.map(item => stableNormalize(item));
  }
  if (isObject(value)) {
    const normalized = {};
    Object.keys(value)
      .sort((a, b) => a.localeCompare(b))
      .forEach(key => {
        normalized[key] = stableNormalize(value[key]);
      });
    return normalized;
  }
  return value;
}

function optionsEqual(left, right) {
  return JSON.stringify(stableNormalize(left || {})) === JSON.stringify(stableNormalize(right || {}));
}

export default class {

  /**
   *
   * @param {PersistentCityModel} parent
   * @param data
   */
  constructor(parent, data) {
    this.parent = parent;
    this.saving = false;
    this._hasListingIdField = data != null && 'listingId' in data;
    this._data = {
      dateSent: null,
      sentByUser: null,
      sendNote: null,
      dateCancelled: null,
      cancelledByUser: null,
      cancelNote: null,
      status: STATUS.PRINTED,
      ...createCorrespondenceData({
        id: uuidv4(),
        dateCreated: new Date(),
        createdByUser: parent.currentUser,
        template: data?.template || null,
        options: data?.options || {}
      }),
      ...data
    };
  }

  get printOption() {
    return {
      template: this.template,
      options: this.options
    };
  }

  get printOptions() {
    return this.parent?.city?.getLetters() || [];
  }

  get label() {
    const printOption = this.printOptions.find(option => {
      if (option.template === this.template) {
        if (optionsEqual(option.options, this.options)) {
          return true;
        }
      }
      return false;
    });
    return printOption?.label || 'Generic Letter';
  }

  async remove() {
    return this.parent.removeCorrespondence(this);
  }

  async send(note) {
    const result = transitionToSent(this._data, {
      dateSent: new Date(),
      sentByUser: this.parent.currentUser,
      note
    });

    if (!result.success) {
      throw new Error(result.error);
    }

    this._data = result.data;
    return this.parent.updateCorrespondence(this);
  }

  async unsend(note) {
    const result = transitionFromSent(this._data, { note });

    if (!result.success) {
      // Silent failure for backward compatibility
      return this;
    }

    this._data = result.data;
    return this.parent.updateCorrespondence(this);
  }

  async void(note) {
    this.cancel(note);
  }

  async cancel(note) {
    const result = transitionToCancelled(this._data, {
      dateCancelled: new Date(),
      cancelledByUser: this.parent.currentUser,
      note
    });

    if (!result.success) {
      throw new Error(result.error);
    }

    this._data = result.data;
    return this.parent.updateCorrespondence(this);
  }

  async uncancel() {
    const result = transitionFromCancelled(this._data);

    if (!result.success) {
      // Silent failure for backward compatibility
      return this;
    }

    this._data = result.data;
    return this.parent.updateCorrespondence(this);
  }

  get id() {
    return this._data.id;
  }

  get dateCreated() {
    return this._data.datePrinted || this._data.dateCreated;
  }

  get dateSent() {
    return this._data.dateSent;
  }

  get dateCancelled() {
    return this._data.dateCancelled;
  }

  get createdByUser() {
    return this._data.printedByUser || this._data.createdByUser;
  }

  get sentByUser() {
    return this._data.sentByUser;
  }

  get cancelledByUser() {
    return this._data.cancelledByUser;
  }

  get sendNote() {
    return this._data.sendNote;
  }

  get cancelNote() {
    return this._data.cancelNote;
  }

  get status() {
    return this._data.status;
  }

  get isSent() {
    return checkIsSent(this._data);
  }

  get isCancelled() {
    return checkIsCancelled(this._data);
  }

  get isPrinted() {
    return checkIsPrinted(this._data);
  }

  get template() {
    return this._data.template;
  }

  get options() {
    return this._data.options;
  }

  get listingId() {
    return this._data.listingId || null;
  }

  get hasListingIdField() {
    return this._hasListingIdField;
  }

  async updateListingId(listingId) {
    this._data.listingId = listingId || null;
    return this.parent.updateCorrespondence(this);
  }

  serialize() {
    return { ...this._data };
  }
}
